<template>
  <content-panel>
    <template v-slot:search>
      <search-box class="search-box">
        <el-form
          :model="form"
          :inline="true"
          class="period-query-form"
          size="mini"
          label-width="120px"
          @submit.native.prevent="search"
        >
          <el-form-item
            label="数据日期"
            required
          >
            <el-date-picker
              v-model="form.dataDate"
              value-format="yyyy-MM-dd"
              type="date"
              :clearable="false"
              :picker-options="pickerOptions"
              @change="dateChanged"
            />
          </el-form-item>

          <el-form-item
            label="时间版本"
            required
          >
            <el-select
              v-model="form.timeVersion"
              :clearable="false"
              :loading="versionLoading"
              placeholder="请选择时间版本"
              :disabled="versionLoading || !timeOptions.length"
            >
              <el-option
                v-for="version in timeOptions"
                :key="version.value"
                :label="version.label"
                :value="version.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item :label="isPackage ? '包件编码' : '商品编码'">
            <el-input
              v-model.trim="form[isPackage ? 'packCode' : 'skuCode']"
              clearable
              :placeholder="`请输入${isPackage ? '包件编码' : '商品编码'}`"
            />
          </el-form-item>

          <el-form-item :label="isPackage ? '包件名称' : '商品名称'">
            <el-input
              v-model.trim="form[isPackage ? 'packName' : 'skuName']"
              clearable
              :placeholder="`请输入${isPackage ? '包件名称' : '商品名称'}`"
            />
          </el-form-item>

          <template v-if="isPackage">
            <el-form-item
              v-for="filter in filters"
              :key="filter.prop"
              :label="filter.label"
            >
              <el-select
                v-model="form[filter.prop]"
                clearable
                placeholder="全部"
              >
                <el-option
                  v-for="item in filter.items"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </template>
        </el-form>

        <template v-slot:right>
          <el-button
            type="primary"
            size="small"
            native-type="submit"
            :disabled="versionLoading || !form.timeVersion"
            :loading="loading"
            @click="search"
          >
            查询
          </el-button>
          <el-button
            size="small"
            class="orange-btn"
            @click="reset"
          >
            重置
          </el-button>
        </template>
      </search-box>
    </template>

    <table-panel ref="tablePanel">
      <template v-slot:header-left>
        {{ isPackage ? '包件发货周期查询（仅电商渠道）' : 'SKU 发货周期查询' }}
      </template>

      <template v-slot:header-right>
        <el-button
          v-if="!isPackage"
          type="primary"
          size="small"
          @click="$router.push({ name: 'SkuFixedPeriod' })"
        >
          发货周期配置
        </el-button>
        <el-button
          size="small"
          :disabled="!applied.dataDate || !applied.timeVersion"
          :loading="exportLoading"
          @click="exportExcel()"
        >
          导出
        </el-button>
        <el-button
          v-if="isPackage"
          size="small"
          :disabled="!applied.dataDate || !applied.timeVersion"
          :loading="detailExportLoading"
          @click="exportExcel(true)"
        >
          计算详情导出
        </el-button>
      </template>

      <el-table
        v-loading="loading || versionLoading"
        :data="rows"
        :height="maxTableHeight"
        border
        size="small"
        :header-cell-style="{ backgroundColor: '#F4F8FB' }"
        empty-text="所选版本暂无符合条件的数据"
        element-loading-text="数据加载中"
      >
        <el-table-column
          :prop="isPackage ? 'packCode' : 'skuCode'"
          :label="isPackage ? '包件编码' : '商品编码（SKU）'"
          min-width="150"
          show-overflow-tooltip
        />
        <el-table-column
          :prop="isPackage ? 'packName' : 'skuName'"
          :label="isPackage ? '包件名称' : '商品名称'"
          min-width="190"
          show-overflow-tooltip
        />

        <template v-if="isPackage">
          <el-table-column
            v-for="column in packageColumns"
            :key="column.prop"
            :prop="column.prop"
            :label="column.label"
            min-width="140"
            show-overflow-tooltip
          />
          <el-table-column label="基础发货周期" align="center">
            <el-table-column label="发货周期" width="110" align="center">
              <template slot-scope="scope">
                <el-tag type="primary" size="small" @click="detail(scope.row)">
                  {{ scope.row.exceptionCode === 'NOT_SHELF' ? '不计算' : periodText(scope.row.regularDeliveryCycle) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="快递发货周期" width="115" align="center">
              <template slot-scope="scope">
                <el-tag type="primary" size="small">
                  {{ !hasCycle(scope.row.expressDeliveryCycle) ? '-' : periodText(scope.row.expressDeliveryCycle) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="物流发货周期" width="115" align="center">
              <template slot-scope="scope">
                <el-tag type="primary" size="small">
                  {{ !hasCycle(scope.row.logisticsDeliveryCycle) ? '-' : periodText(scope.row.logisticsDeliveryCycle) }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table-column>
          <el-table-column label="活动发货周期" align="center">
            <el-table-column
              prop="activityRegularDeliveryCycle"
              label="活动发货周期"
              width="130"
              align="center">
              <template slot-scope="scope">
                <el-tag type="primary" size="small">
                {{ hasCycle(scope.row.activityRegularDeliveryCycle) ? periodText(scope.row.activityRegularDeliveryCycle) : '-' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="activityExpressDeliveryCycle"
              label="活动快递发货周期"
              width="145"
              align="center">
              <template slot-scope="scope">
                <el-tag type="primary" size="small">
                {{ hasCycle(scope.row.activityExpressDeliveryCycle) ? periodText(scope.row.activityExpressDeliveryCycle) : '-' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="activityLogisticsDeliveryCycle"
              label="活动物流发货周期"
              width="145"
              align="center">
              <template slot-scope="scope">
                <el-tag type="primary" size="small">
                {{ hasCycle(scope.row.activityLogisticsDeliveryCycle) ? periodText(scope.row.activityLogisticsDeliveryCycle) : '-' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table-column>
          <el-table-column
            prop="sevenDayFlag"
            label="满足7天发货"
            width="115"
          />
        </template>

        <template v-else>
          <el-table-column
            prop="logisticsModeName"
            label="发货方式"
            width="140"
            show-overflow-tooltip
          />
          <el-table-column label="发货周期" width="110">
            <template slot-scope="scope">
              <el-button type="text" @click="detail(scope.row)">
                {{ periodText(scope.row.deliveryCycle) }}
              </el-button>
            </template>
          </el-table-column>
        </template>

        <el-table-column
          prop="exceptionMessage"
          label="异常说明"
          min-width="180"
          show-overflow-tooltip
        />
        <el-table-column
          prop="calculateTime"
          :label="isPackage ? '计算时间' : '生成时间'"
          width="175"
        />
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="detail(scope.row)">
              {{ isPackage ? '计算明细' : '查看明细' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <template v-slot:footer>
        <div class="period-pagination">
          <el-pagination
            :current-page.sync="page"
            :page-size.sync="size"
            :page-sizes="[10, 30, 50, 100]"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            background
            @size-change="search"
            @current-change="load"
          />
        </div>
      </template>
    </table-panel>

    <period-detail
      v-if="detailVisible"
      :visible.sync="detailVisible"
      :record="selected"
      :type="isPackage ? 'package' : 'sku'"
    />
  </content-panel>
</template>

<script>
import moment from 'moment'
import pagePathMixin from '@/utils/page-path-mixin'
import tableMixin from '@/utils/tableMixin'
import {
  queryFilter,
  pageRequest,
  options,
  periodText,
  hasCycle,
  errorText,
  responseData
} from './model'
import PeriodDetail from './components/PeriodDetail'

const defaults = () => ({
  dataDate: '',
  timeVersion: '',
  packCode: '',
  packName: '',
  skuCode: '',
  skuName: '',
  lifeCycleType: '',
  productionLogisticsType: '',
  supplyChannel: '',
  deliveryCycle: '',
  sevenDayFlag: ''
})

export default {
  name: 'ShippingPeriodQuery',
  mixins: [pagePathMixin, tableMixin],
  components: { PeriodDetail },
  data () {
    return {
      form: defaults(),
      applied: {},
      rows: [],
      total: 0,
      page: 1,
      size: 10,
      loading: false,
      versionLoading: false,
      exportLoading: false,
      detailExportLoading: false,
      error: '',
      requestId: 0,
      versionId: 0,
      initId: 0,
      timeOptions: [],
      dict: {},
      selected: null,
      detailVisible: false,
      packageColumns: [
        {
          prop: 'lifeCycleTypeName',
          label: '上架分类'
        },
        {
          prop: 'productionLogisticsTypeName',
          label: '生产物流分类'
        },
        {
          prop: 'supplyChannelName',
          label: '供应渠道'
        }
      ],
      pickerOptions: {
        disabledDate: date => (
          date > moment().endOf('day').toDate() ||
          date < moment().subtract(3, 'months').startOf('day').toDate()
        )
      }
    }
  },
  computed: {
    isPackage () {
      return this.$route.meta.periodType === 'package'
    },
    filters () {
      return [
        ['lifeCycleType', '上架分类', 'lifeCycleOptions'],
        ['productionLogisticsType', '生产物流分类', 'productionOptions'],
        ['supplyChannel', '供应渠道', 'channelOptions'],
        ['deliveryCycle', '发货周期', 'deliveryCycleOptions'],
        ['sevenDayFlag', '是否满足7天发货', 'sevenDayOptions']
      ].map(([prop, label, key]) => ({
        prop,
        label,
        items: options(this.dict[key])
      }))
    }
  },
  watch: {
    '$route.path' () {
      this.initialize()
    }
  },
  created () {
    this.initialize()
  },
  beforeDestroy () {
    this.requestId++
    this.versionId++
    this.initId++
  },
  methods: {
    periodText,
    hasCycle,
    async initialize () {
      const id = ++this.initId

      this.detailVisible = false
      this.form = defaults()
      this.applied = {}
      this.dict = {}
      this.error = ''

      const filters = this.isPackage
        ? this.$q.shippingPeriod.filters()
          .then(responseData)
          .then(data => {
            if (id === this.initId) {
              this.dict = data || {}
            }
          })
          .catch(error => {
            if (id === this.initId) {
              this.error = errorText(error)
            }
          })
        : Promise.resolve()

      await Promise.all([this.dateChanged(), filters])
    },
    async dateChanged () {
      const id = ++this.versionId

      this.requestId++
      this.applied = {}
      this.loading = false
      this.versionLoading = true
      this.rows = []
      this.total = 0
      this.form.timeVersion = ''
      this.timeOptions = []
      this.error = ''

      try {
        const data = responseData(
          await this.$q.shippingPeriod.versions({
            params: this.form.dataDate
              ? { dataDate: this.form.dataDate }
              : {}
          })
        )

        if (id !== this.versionId) return

        this.form.dataDate = data.dataDate || this.form.dataDate
        this.timeOptions = (data.timeVersions || []).map(item => (
          typeof item === 'string'
            ? { value: item, label: item }
            : {
              value: item.timeVersion,
              label: item.timeVersion,
              versionNo: item.versionNo
            }
        ))
        this.form.timeVersion = data.latestTimeVersion || (
          this.timeOptions[0] || {}
        ).value || ''

        if (this.form.timeVersion) {
          await this.search()
        }
      } catch (error) {
        if (id === this.versionId) {
          this.error = errorText(error)
        }
      } finally {
        if (id === this.versionId) {
          this.versionLoading = false
        }
      }
    },
    search () {
      try {
        this.applied = queryFilter(
          this.isPackage ? 'package' : 'sku',
          this.form
        )
      } catch (error) {
        this.error = errorText(error)
        return
      }

      this.page = 1
      return this.load()
    },
    async load () {
      if (!this.applied.dataDate || !this.applied.timeVersion) return

      const id = ++this.requestId
      this.loading = true
      this.error = ''

      try {
        const data = responseData(
          await this.$q.shippingPeriod[
            this.isPackage ? 'packages' : 'skus'
          ]({
            data: pageRequest(this.page, this.size, this.applied)
          })
        )

        if (id !== this.requestId) return

        const version = this.timeOptions.find(
          item => item.value === this.applied.timeVersion
        ) || {}
        this.rows = (data.records || []).map(row => ({
          ...row,
          versionNo: row.versionNo || data.versionNo || version.versionNo
        }))
        this.total = Number(data.total) || 0
      } catch (error) {
        if (id === this.requestId) {
          this.rows = []
          this.total = 0
          this.error = errorText(error)
        }
      } finally {
        if (id === this.requestId) {
          this.loading = false
        }
      }
    },
    async exportExcel (exportDetails = false) {
      const loadingKey = exportDetails ? 'detailExportLoading' : 'exportLoading'

      if (
        this[loadingKey] ||
        !this.applied.dataDate ||
        !this.applied.timeVersion
      ) {
        return
      }

      this[loadingKey] = true

      let reportName = this.isPackage ? 'deliveryCyclePackage' : 'deliveryCycleSku'
      if (exportDetails) {
        reportName = 'packageFullPageExport'
      }

      try {
        const response = await this.$q.common.exportExcel({
          data: {
            appCode: 'ec-pc-api',
            paramMap: { ...this.applied },
            reportName
          }
        })

        if (
          !response ||
          response.success === false ||
          (response.code && !['200', '000000'].includes(String(response.code)))
        ) {
          throw new Error((response && response.message) || '提交导出任务失败')
        }

        this.$message.success('导出任务已提交，请在导出列表中查看')
      } catch (error) {
        this.$message.error(errorText(error))
      } finally {
        this[loadingKey] = false
      }
    },
    reset () {
      this.initialize()
    },
    detail (row) {
      this.selected = {
        ...row,
        ...(this.isPackage
          ? {
            dataDate: this.applied.dataDate,
            timeVersion: this.applied.timeVersion
          }
          : {})
      }
      this.detailVisible = true
    }
  }
}
</script>

<style src="./style.css"></style>

<style scoped>
.period-query-form {
  display: flex;
  flex-wrap: wrap;
}
</style>
